/**
 * Shared API contract types (DTOs) between apps/api (Hono) and apps/web (React).
 * Keep these transport-shaped (JSON-serialisable) — no Date objects, ISO strings.
 */

import type { AccountRole } from './roles.js';
import type {
  DocType,
  DocStatus,
  GcuProcessStatus,
  GcuStep,
  QuestionnaireRound,
  QuestionnaireStatus,
  QuestionType,
  QuestionSource,
  DeliveryChannel,
  DeliveryStatus,
  ReportStatus,
  ReportArea,
  SwotAxis,
  HealthStatus,
  FindingSource,
  ProgramArea,
  RecommendationPriority,
  RecommendationStatus,
  RecommendationSource,
  EngagementStatus,
  SessionStatus,
} from './gcu.js';

/** Uniform success/error envelope returned by every API route. */
export type ApiOk<T> = { ok: true; data: T };
export type ApiErr = { ok: false; error: { code: string; message: string } };
export type ApiResult<T> = ApiOk<T> | ApiErr;

/** The authenticated principal (account roles only; PIC is never authenticated). */
export interface SessionUser {
  id: string;
  email: string;
  name: string;
  avatarUrl: string | null;
  role: AccountRole;
  /** Set for Owner (their company). null for internal Coach/Admin. */
  companyId: string | null;
}

export interface CompanyProfile {
  id: string;
  name: string;
  industry: string | null;
  employeeCount: number | null;
  ownerName: string | null;
  ownerEmail: string | null;
  ownerWhatsapp: string | null;
  onboardedAt: string | null;
}

/** A PIC mapping row (no account; receives questionnaire via magic-link). */
export interface PicContact {
  id: string;
  companyId: string;
  name: string;
  division: string;
  email: string | null;
  whatsapp: string | null;
  /** invite lifecycle state surfaced in CR1b table. */
  status: 'pending' | 'invited' | 'in_progress' | 'done';
}

// ---- UU PDP consent (Ps. 20 basis + Ps. 56(4) cross-border transfer) ----

/**
 * Current privacy-policy version shown to data subjects. Bump this (dated) whenever
 * the privacy notice / sub-processor list materially changes, so captured consent
 * records which version the subject accepted. Mirrors compliance/kebijakan-privasi.md.
 */
export const PRIVACY_POLICY_VERSION = '2026-06-21';

/** Consent evidence surfaced to the client (companies/pics consent columns). */
export interface ConsentInfo {
  consentVersion: string | null;
  consentAt: string | null;
}

// ---- Request payloads (CR1) ----

export interface UpsertCompanyInput {
  name: string;
  industry?: string | null;
  employeeCount?: number | null;
  ownerName?: string | null;
  ownerWhatsapp?: string | null;
  /**
   * The privacy-policy version the Owner explicitly accepted at onboarding
   * (must equal PRIVACY_POLICY_VERSION). Recorded as UU PDP consent evidence.
   */
  acceptPolicyVersion?: string | null;
}

export interface UpsertPicInput {
  id?: string;
  name: string;
  division: string;
  email?: string | null;
  whatsapp?: string | null;
}

/** POST /q/:token/submit — the PIC's questionnaire submission. */
export interface PicSubmitInput {
  answers?: AnswerInput[];
  /**
   * The privacy-policy version the PIC explicitly accepted at submit (must equal
   * PRIVACY_POLICY_VERSION). Recorded as UU PDP consent evidence (Ps. 56(4)
   * cross-border transfer), mirroring the Owner onboarding gate.
   */
  acceptPolicyVersion?: string | null;
}

// ---- GCU Intake / documents (CR2) ----

/**
 * Default max single-upload size in bytes (25 MB; Director 2026-06-21). Shared so
 * the web client and the API server enforce ONE value: the API uses it as the
 * server-side default when MAX_UPLOAD_BYTES is unset, and the web client uses it
 * to pre-validate before requesting a presigned PUT.
 */
export const MAX_UPLOAD_BYTES_DEFAULT = 26214400;

/** A document row as surfaced to the client. NEVER includes the R2 key or a URL. */
export interface DocumentSummary {
  id: string;
  companyId: string;
  docType: DocType;
  filename: string;
  contentType: string | null;
  sizeBytes: number | null;
  status: DocStatus;
  pageCount: number | null;
  parseError: string | null;
  /** ISO timestamps (null when not yet set). */
  uploadedAt: string | null;
  retentionUntil: string | null;
  createdAt: string;
}

/** POST /companies/:id/documents — request a presigned direct-to-R2 upload. */
export interface InitiateUploadInput {
  docType: DocType;
  filename: string;
  contentType: string;
  sizeBytes: number;
}

/** POST /companies/:id/documents — presigned PUT result (client uploads directly to R2). */
export interface InitiateUploadResult {
  documentId: string;
  /** Short-lived presigned S3 PUT URL. The client PUTs the file bytes here. */
  uploadUrl: string;
  /** Server-generated object key (returned for confirm; never client-set). */
  r2Key: string;
  expiresInSeconds: number;
}

/** GET /companies/:id/gcu — process state + document roll-up for the stepper. */
export interface GcuProcessState {
  currentStep: GcuStep;
  stepNumber: number;
  totalSteps: number;
  status: GcuProcessStatus;
  analysisRequestedAt: string | null;
  documentCounts: {
    byType: Partial<Record<DocType, number>>;
    byStatus: Partial<Record<DocStatus, number>>;
  };
}

// ---- Questionnaires (CR3) ----

/** A questionnaire row as surfaced in list views. */
export interface QuestionnaireSummary {
  id: string;
  companyId: string;
  round: QuestionnaireRound;
  /** PIC round → the target PIC; owner round → null. */
  picId: string | null;
  /** Convenience denormalisation for the distribution / list UI. */
  picName: string | null;
  division: string | null;
  status: QuestionnaireStatus;
  title: string;
  questionCount: number;
  generationError: string | null;
  /** ISO timestamps (null until set). */
  sentAt: string | null;
  completedAt: string | null;
  createdAt: string;
}

/** A single question in the editor / PIC form. */
export interface QuestionDTO {
  id: string;
  orderIndex: number;
  type: QuestionType;
  text: string;
  options: string[] | null;
  required: boolean;
  source: QuestionSource;
}

/** GET …/questionnaires/:qid — the questionnaire plus its ordered questions. */
export interface QuestionnaireDetail {
  questionnaire: QuestionnaireSummary;
  questions: QuestionDTO[];
}

/** One answer in an autosave / submit payload. `value` shape depends on type. */
export interface AnswerInput {
  questionId: string;
  value: unknown;
}

/** An already-saved answer surfaced back to the respondent (resume support). */
export interface SavedAnswer {
  questionId: string;
  value: unknown;
  /** ISO timestamp; null = autosaved draft, set = submitted. */
  submittedAt: string | null;
}

/** One PIC's row in the distribution rollup. */
export interface DistributionRow {
  picId: string;
  picName: string | null;
  division: string;
  /** Channels the link was distributed over (derived from pic contact + deliveries). */
  channels: DeliveryChannel[];
  /** Latest delivery status across channels ('queued' | 'sent' | 'failed' | null). */
  deliveryStatus: DeliveryStatus | null;
  /** ISO timestamp the questionnaire was sent (null until sent). */
  sentAt: string | null;
  /** PIC answering progress: 'pending' | 'invited' | 'in_progress' | 'done'. */
  status: PicContact['status'];
  /** 0..100 — answered required questions / total required. */
  progressPct: number;
}

// ---- GCU Report (CR4) ----

/** Per-area health scores (each 0–100). Fixed 4-key shape. */
export type AreaScores = Record<ReportArea, number>;

/** One initial coaching recommendation theme (feeds CR5; opaque list in CR4). */
export interface RecommendationDTO {
  title: string;
  area: ReportArea;
  rationale: string;
  /** Optional suggested program hint (CR5 maps it to the real catalog). */
  suggestedProgram?: string | null;
}

/** A single SWOT finding row in the report editor / view. */
export interface ReportFindingDTO {
  id: string;
  axis: SwotAxis;
  text: string;
  orderIndex: number;
  source: FindingSource;
}

/** A report row as surfaced in list / summary views (no R2 key / URL). */
export interface ReportSummary {
  id: string;
  companyId: string;
  gcuProcessId: string;
  status: ReportStatus;
  overallScore: number | null;
  areaScores: AreaScores | null;
  healthStatus: HealthStatus | null;
  /** True once the PDF has been composed (pdfR2Key present). */
  pdfReady: boolean;
  generationError: string | null;
  /** ISO timestamps (null until set). */
  finalizedAt: string | null;
  sentAt: string | null;
  createdAt: string;
}

/** GET …/reports/:reportId — the report plus its ordered SWOT findings. */
export interface ReportDetail {
  report: ReportSummary;
  executiveSummary: string | null;
  keyFindings: string[];
  recommendations: RecommendationDTO[];
  findings: ReportFindingDTO[];
}

/** PATCH …/reports/:reportId — editable header fields (internal review). */
export interface ReportReviewInput {
  executiveSummary?: string;
  overallScore?: number;
  areaScores?: Partial<AreaScores>;
  healthStatus?: HealthStatus;
  keyFindings?: string[];
  recommendations?: RecommendationDTO[];
}

/** POST …/reports/:reportId/findings — add a SWOT finding (team_added). */
export interface AddFindingInput {
  axis: SwotAxis;
  text: string;
  orderIndex?: number;
}

/** PATCH …/findings/:findingId — edit a SWOT finding (team_edited). */
export interface EditFindingInput {
  axis?: SwotAxis;
  text?: string;
  orderIndex?: number;
}

// ---- Coaching (CR5) ----

/** A coaching program from the global catalog (S5; no tenant data). */
export interface ProgramDTO {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  area: ProgramArea;
  keywords: string[];
  defaultSessions: number;
  active: boolean;
}

/** One program recommendation joined to its catalog program (poc cr5a card). */
export interface RecommendationDetailDTO {
  id: string;
  reportId: string;
  programId: string;
  /** Denormalised catalog fields for the card. */
  programSlug: string;
  programName: string;
  programArea: ProgramArea;
  programDescription: string | null;
  defaultSessions: number;
  priority: RecommendationPriority;
  matchPercent: number;
  /** "Mengapa rekomendasi ini". */
  rationale: string | null;
  source: RecommendationSource;
  status: RecommendationStatus;
  createdAt: string;
}

/** POST …/recommendations/generate — result. */
export interface GenerateRecommendationsResult {
  reportId: string;
  count: number;
}

/** PATCH …/recommendations/:recId — coach adjust (internal). */
export interface AdjustRecommendationInput {
  priority?: RecommendationPriority;
  rationale?: string;
  status?: Extract<RecommendationStatus, 'suggested' | 'offered' | 'dismissed'>;
}

/** A coaching session (TRACKED record, not a booking). */
export interface SessionDTO {
  id: string;
  engagementId: string;
  sessionNumber: number;
  title: string;
  status: SessionStatus;
  /** Coach memo date (ISO), NOT a booked slot. null = undated. */
  scheduledFor: string | null;
  completedAt: string | null;
  notes: string | null;
  createdAt: string;
}

/** POST …/engagements/:engId/sessions — create a session (internal). */
export interface AddSessionInput {
  sessionNumber?: number;
  title: string;
  scheduledFor?: string | null;
  notes?: string | null;
}

/** PATCH …/sessions/:sessionId — update a session (internal). */
export interface UpdateSessionInput {
  title?: string;
  notes?: string | null;
  scheduledFor?: string | null;
  status?: SessionStatus;
}

/** Per-area before/after delta inside a progress snapshot. */
export type AreaDeltas = Partial<Record<ProgramArea, { before: number; after: number }>>;

/** A coach-assessed progress snapshot (the 72→78 record; NOT a re-diagnosis). */
export interface ProgressSnapshotDTO {
  id: string;
  engagementId: string | null;
  label: string;
  scoreBefore: number;
  scoreAfter: number;
  areaDeltas: AreaDeltas;
  note: string | null;
  createdAt: string;
}

/** POST …/engagements/:engId/progress — record a snapshot (internal). */
export interface RecordProgressInput {
  label: string;
  scoreBefore: number;
  scoreAfter: number;
  areaDeltas?: AreaDeltas;
  note?: string | null;
}

/** PATCH …/engagements/:engId — set status / assign coach (internal). */
export interface UpdateEngagementInput {
  status?: EngagementStatus;
  coachUserId?: string | null;
}

/** An engagement with its program + session rollup (poc cr5b program bars). */
export interface EngagementDTO {
  id: string;
  companyId: string;
  programId: string;
  programName: string;
  programArea: ProgramArea;
  recommendationId: string | null;
  coachUserId: string | null;
  coachName: string | null;
  status: EngagementStatus;
  sessionsDone: number;
  sessionsTotal: number;
  /** 0–100: sessionsDone / max(sessionsTotal, defaultSessions). */
  progressPct: number;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
}

/** GET …/engagements/:engId — engagement + ordered sessions + its snapshots. */
export interface EngagementDetailDTO {
  engagement: EngagementDTO;
  sessions: SessionDTO[];
  snapshots: ProgressSnapshotDTO[];
}

/** A "next task" surfaced on the Owner dashboard (poc cr5b "Tugas Berikutnya"). */
export interface NextTaskDTO {
  engagementId: string;
  sessionId: string;
  sessionNumber: number;
  title: string;
  scheduledFor: string | null;
}

/** GET …/coaching/overview — the Owner dashboard aggregate (poc cr5b). */
export interface CoachingOverviewDTO {
  activeEngagements: EngagementDTO[];
  /** Next scheduled sessions across engagements, soonest first. */
  nextTasks: NextTaskDTO[];
  /** Latest company-level (engagementId NULL) progress snapshot. */
  latestProgress: ProgressSnapshotDTO | null;
}

// ---- Admin console (CR6) ----

/**
 * One row in the internal clients list (GET /admin/clients) — the keystone
 * retrofit that feeds the CR3–CR5 `:companyId` deep-links. admin = all companies;
 * coach = assigned only. NEVER includes owner PII (email/whatsapp).
 */
export interface AdminClientRow {
  companyId: string;
  name: string;
  industry: string | null;
  employeeCount: number | null;
  /** Assigned coach (first assignment) — name + id for the deep-link / filter. */
  coachName: string | null;
  coachUserId: string | null;
  /** Current GCU step (null if the process has not started). */
  gcuStep: GcuStep | null;
  gcuStepNumber: number | null;
  gcuStatus: GcuProcessStatus | null;
  /** Active report status (null until a report exists). */
  reportStatus: ReportStatus | null;
  /** ISO timestamp of the latest GCU process activity (or company update). */
  updatedAt: string;
}

/** GET /admin/clients — paginated client list (keyset on (created_at, id)). */
export interface AdminClientsPage {
  items: AdminClientRow[];
  nextCursor: string | null;
}

/** A Nine Co internal staff member (coach/admin) with assignment count (GET /admin/coaches). */
export interface CoachSummary {
  userId: string;
  name: string;
  email: string;
  role: Extract<AccountRole, 'coach' | 'admin'>;
  assignedCount: number;
}

/** A company a coach is assigned to (GET /admin/coaches/:userId/assignments). */
export interface CoachAssignmentRow {
  assignmentId: string;
  companyId: string;
  companyName: string;
}

/**
 * One company in the coach→companies drilldown
 * (GET /admin/coaches/:coachUserId/companies). Ordered by assignedAt desc.
 */
export interface CoachCompanyRow {
  companyId: string;
  companyName: string;
  assignmentId: string;
  /** ISO-8601 — when the coach was assigned to this company (coach_assignments.createdAt). */
  assignedAt: string;
}

/** POST /admin/companies/:id/assign-coach. */
export interface AssignCoachInput {
  coachUserId: string;
}

/** A global question-bank row (GET /admin/question-bank — admin/coach read). */
export interface QuestionBankRow {
  id: string;
  division: string;
  docType: DocType | null;
  round: QuestionnaireRound;
  type: QuestionType;
  text: string;
  options: string[] | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

/** POST /admin/question-bank — create a global library question (admin-only). */
export interface CreateQuestionBankInput {
  division: string;
  docType?: DocType | null;
  round: QuestionnaireRound;
  type: QuestionType;
  text: string;
  options?: string[] | null;
}

/** PATCH /admin/question-bank/:qid — edit a library question (admin-only). */
export interface EditQuestionBankInput {
  division?: string;
  docType?: DocType | null;
  round?: QuestionnaireRound;
  type?: QuestionType;
  text?: string;
  options?: string[] | null;
  active?: boolean;
}

/** One bar of the GCU step distribution (admin monitor / analytics). */
export interface StepDistributionRow {
  step: GcuStep;
  stepNumber: number;
  count: number;
}

/** GET /admin/analytics — cross-company aggregate (admin-only). */
export interface AdminAnalytics {
  gcuCompleted: number;
  gcuInProgress: number;
  /** Mean days from first step event → completion (null when no completed runs). */
  avgDurationDays: number | null;
  /** Mean hours spent in each stage, from gcu_step_events (LAG window). */
  avgTimePerStage: { step: GcuStep; stepNumber: number; avgHours: number }[];
  clientsPerIndustry: { industry: string | null; count: number }[];
  conversionToCoaching: { reportsSent: number; engagementsStarted: number; pct: number };
  /** Mean(scoreAfter − scoreBefore) over company-level progress snapshots. */
  avgScoreDelta: number | null;
  gcuPerMonth: { month: string; started: number; completed: number }[];
  stageDistribution: StepDistributionRow[];
}

/** One audit-log row in the admin viewer (GET /admin/audit). PII-free by construction. */
export interface AuditRow {
  id: string;
  createdAt: string;
  actorType: string;
  actorId: string | null;
  actorLabel: string | null;
  action: string;
  objectType: string | null;
  objectId: string | null;
  companyId: string | null;
  ip: string | null;
  metadata: Record<string, unknown> | null;
}

/** GET /admin/audit — keyset-paginated audit page (cursor on (created_at, id)). */
export interface AuditPage {
  items: AuditRow[];
  nextCursor: string | null;
}

export const API_ERROR = {
  UNAUTHENTICATED: 'unauthenticated',
  FORBIDDEN: 'forbidden',
  NOT_FOUND: 'not_found',
  VALIDATION: 'validation_error',
  MAGIC_LINK_INVALID: 'magic_link_invalid',
  MAGIC_LINK_EXPIRED: 'magic_link_expired',
  RATE_LIMITED: 'rate_limited',
  INTERNAL: 'internal_error',
  // CR2 — GCU Intake / documents.
  /** Hard-lock retention policy active and retentionUntil is still in the future. */
  RETENTION_LOCKED: 'retention_locked',
  /** Confirm could not verify the uploaded object (missing in R2 or size mismatch). */
  UPLOAD_OBJECT_MISMATCH: 'upload_object_mismatch',
  /** analyze found no documents in the 'uploaded' state to enqueue. */
  NO_ELIGIBLE_DOCUMENTS: 'no_eligible_documents',
  /** The company has reached MAX_DOCS_PER_COMPANY non-deleted documents. */
  DOC_QUOTA_EXCEEDED: 'doc_quota_exceeded',
  // CR3 — Questionnaires.
  /** A questionnaire is not in a state that permits the requested action (e.g. no questions to approve). */
  QUESTIONNAIRE_NOT_READY: 'questionnaire_not_ready',
  /** The respondent already submitted this questionnaire (idempotent re-submit). */
  ALREADY_SUBMITTED: 'already_submitted',
  /** Preconditions for this round are not met (e.g. no indexed docs / no PICs). */
  ROUND_NOT_READY: 'round_not_ready',
  // CR4 — GCU Report.
  /** The report (or its PDF) is not in a state that permits the requested action. */
  REPORT_NOT_READY: 'report_not_ready',
  /** The report is frozen (approved/sent) and can no longer be edited. */
  REPORT_FROZEN: 'report_frozen',
  /** The owner verification round is not completed, so the report cannot be generated. */
  OWNER_ROUND_INCOMPLETE: 'owner_round_incomplete',
  /** The report PDF has not been composed yet (cannot send / download). */
  PDF_NOT_READY: 'pdf_not_ready',
  // CR5 — Rekomendasi & Coaching.
  /** Recommendations can only be generated from a finalized (sent) report. */
  NO_FINALIZED_REPORT: 'no_finalized_report',
  /** A program recommendation row was not found (company-scoped). */
  RECOMMENDATION_NOT_FOUND: 'recommendation_not_found',
  /** A coaching engagement was not found (company-scoped). */
  ENGAGEMENT_NOT_FOUND: 'engagement_not_found',
  /** A coaching session was not found (company/engagement-scoped). */
  SESSION_NOT_FOUND: 'session_not_found',
  /** A catalog program was not found / inactive. */
  PROGRAM_NOT_FOUND: 'program_not_found',
  /** The recommendation is already selected (frozen) — cannot adjust again. */
  ALREADY_SELECTED: 'already_selected',
  // CR6 — Admin console.
  /** assign-coach: the (coach, company) assignment already exists. */
  ALREADY_ASSIGNED: 'already_assigned',
  /** assign-coach: the target user is not a coach/admin (cannot be assigned). */
  COACH_REQUIRED: 'coach_required',
  /** A coach assignment row was not found. */
  ASSIGNMENT_NOT_FOUND: 'assignment_not_found',
  /** A global question-bank row was not found. */
  QUESTION_BANK_NOT_FOUND: 'question_bank_not_found',
  /** reanalyze: the document is not in a re-analyzable state (only failed|needs_ocr). */
  NOT_REANALYZABLE: 'not_reanalyzable',
  /** recompose: the report is not in a recomposable state (approved + no PDF). */
  NOT_RECOMPOSABLE: 'not_recomposable',
} as const;
export type ApiErrorCode = (typeof API_ERROR)[keyof typeof API_ERROR];
